// Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';

interface AgeGroupData {
  range: string;
  count: number;
  percentage: number;
}

interface AgeGroupsBarChartProps {
  data: AgeGroupData[];
}

export const AgeGroupsBarChart = ({ data }: AgeGroupsBarChartProps) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);

  const config = useChartConfig();

  // Transform data for chart
  const chartData = data.map((item) => ({
    ageGroup: t(`demographics.ageRanges.${item.range}`) || item.range,
    count: item.count,
    percentage: item.percentage,
  }));

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('demographics.ageGroupsDistribution')}
        </h2>
        <p className="text-sm text-gray-700">
          {t('demographics.ageGroupsDistributionDescription')}
        </p>
      </div>

      {/* Chart */}
      <ChartContent>
        <style>
          {`
            .recharts-legend-item .recharts-surface {
              margin-${isRTL ? 'left' : 'right'}: 8px !important;
            }
          `}
        </style>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart
            width={700}
            height={360}
            data={chartData}
            margin={{
              top: 25,
              right: isRTL ? 20 : 20,
              left: isRTL ? -20 : 0,
            }}
            barSize={20}
            onClick={undefined}
            style={config.styles.noOutline}
          >
            <XAxis
              dataKey="ageGroup"
              scale="point"
              padding={{ left: 75, right: 75 }}
              {...config.axis}
            />
            <YAxis {...config.axis} />
            <Tooltip {...config.tooltip} />
            <Legend {...config.legend} />
            <CartesianGrid strokeDasharray="3 3" stroke={config.colors.grid} />
            <Bar
              dataKey="count"
              name={t('dashboard.dataLabels.participants')}
              fill={config.colors.primary}
              background={{ fill: config.colors.barBackground }}
              style={config.styles.noOutline}
              radius={[5, 5, 0, 0]}
              activeBar={{ fill: config.colors.primary, opacity: 0.6 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>
        {t('demographics.ageGroupsDistributionShowing')}
      </ChartFooter>
    </ChartContainer>
  );
};
